
import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function EarningsCalculator() {
  const [spaceType, setSpaceType] = useState('garage');
  const [spaceSize, setSpaceSize] = useState('medium');
  const [location, setLocation] = useState('sydney');
  const [availability, setAvailability] = useState('full-time');

  const calculateEarnings = () => {
    const baseRates: Record<string, number> = {
      garage: 600,
      parking: 400,
      storage: 500,
      workshop: 1000,
      container: 450,
      basement: 550,
      shed: 350,
      warehouse: 1200
    };

    const sizeMultipliers: Record<string, number> = {
      small: 0.7,
      medium: 1.0,
      large: 1.4,
      'extra-large': 1.8
    };

    const locationMultipliers: Record<string, number> = {
      sydney: 1.3,
      melbourne: 1.2,
      brisbane: 1.0,
      perth: 0.9,
      adelaide: 0.85,
      canberra: 1.1,
      hobart: 0.8,
      darwin: 0.75
    };

    const availabilityMultipliers: Record<string, number> = {
      'full-time': 1.0,
      'part-time': 0.6,
      'weekends': 0.3,
      'occasional': 0.4
    };

    const baseRate = baseRates[spaceType] || 500;
    const sizeMultiplier = sizeMultipliers[spaceSize] || 1.0;
    const locationMultiplier = locationMultipliers[location] || 1.0;
    const availabilityMultiplier = availabilityMultipliers[availability] || 1.0;

    const monthlyEarnings = Math.round(
      baseRate * sizeMultiplier * locationMultiplier * availabilityMultiplier
    );

    return {
      monthly: monthlyEarnings,
      yearly: monthlyEarnings * 12,
      weekly: Math.round(monthlyEarnings / 4.33)
    };
  };

  const earnings = calculateEarnings();

  const spaceTypes = [
    { value: 'garage', label: 'Garage', icon: 'ri-home-4-line' },
    { value: 'parking', label: 'Parking Space', icon: 'ri-car-line' },
    { value: 'storage', label: 'Storage Room', icon: 'ri-archive-line' },
    { value: 'workshop', label: 'Workshop', icon: 'ri-tools-line' },
    { value: 'container', label: 'Container', icon: 'ri-inbox-line' },
    { value: 'basement', label: 'Basement', icon: 'ri-building-line' },
    { value: 'shed', label: 'Shed', icon: 'ri-home-2-line' },
    { value: 'warehouse', label: 'Warehouse', icon: 'ri-store-line' }
  ];

  const sizes = [
    { value: 'small', label: 'Small', description: 'Up to 10m²' },
    { value: 'medium', label: 'Medium', description: '10-25m²' },
    { value: 'large', label: 'Large', description: '25-50m²' },
    { value: 'extra-large', label: 'Extra Large', description: '50m²+' }
  ];

  const locations = [
    { value: 'sydney', label: 'Sydney' },
    { value: 'melbourne', label: 'Melbourne' },
    { value: 'brisbane', label: 'Brisbane' },
    { value: 'perth', label: 'Perth' },
    { value: 'adelaide', label: 'Adelaide' },
    { value: 'canberra', label: 'Canberra' },
    { value: 'hobart', label: 'Hobart' },
    { value: 'darwin', label: 'Darwin' }
  ];

  const availabilityOptions = [
    { value: 'full-time', label: 'Full-time', description: 'Available 24/7' },
    { value: 'part-time', label: 'Part-time', description: 'Weekdays only' },
    { value: 'weekends', label: 'Weekends', description: 'Sat-Sun only' },
    { value: 'occasional', label: 'Occasional', description: 'Few days/month' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <div className="bg-gradient-to-br from-green-600 to-green-800 py-16 pt-36">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-white mb-4">
            Earnings Calculator
          </h1>
          <p className="text-xl text-green-100 max-w-2xl mx-auto">
            See how much you could earn by sharing your space on STASHLY
          </p>
        </div>
      </div>

      {/* Calculator */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Input Section */}
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Tell us about your space
              </h2>

              {/* Space Type */}
              <div className="mb-8">
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Space Type
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {spaceTypes.map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setSpaceType(type.value)}
                      className={`p-4 rounded-xl border-2 transition-all cursor-pointer whitespace-nowrap ${
                        spaceType === type.value
                          ? 'border-green-600 bg-green-50'
                          : 'border-gray-200 hover:border-green-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          spaceType === type.value ? 'bg-green-600' : 'bg-gray-100'
                        }`}>
                          <i className={`${type.icon} text-xl ${
                            spaceType === type.value ? 'text-white' : 'text-gray-600'
                          }`}></i>
                        </div>
                        <span className={`font-medium text-sm ${
                          spaceType === type.value ? 'text-green-600' : 'text-gray-700'
                        }`}>
                          {type.label}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Space Size */}
              <div className="mb-8">
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Space Size
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {sizes.map((size) => (
                    <button
                      key={size.value}
                      onClick={() => setSpaceSize(size.value)}
                      className={`p-4 rounded-xl border-2 transition-all cursor-pointer whitespace-nowrap ${
                        spaceSize === size.value
                          ? 'border-green-600 bg-green-50'
                          : 'border-gray-200 hover:border-green-300'
                      }`}
                    >
                      <div className="text-left">
                        <div className={`font-medium text-sm mb-1 ${
                          spaceSize === size.value ? 'text-green-600' : 'text-gray-700'
                        }`}>
                          {size.label}
                        </div>
                        <div className="text-xs text-gray-500">
                          {size.description}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Location */}
              <div className="mb-8">
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Location
                </label>
                <select
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-600 focus:outline-none text-sm cursor-pointer"
                >
                  {locations.map((loc) => (
                    <option key={loc.value} value={loc.value}>
                      {loc.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Availability */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Availability
                </label>
                <div className="space-y-2">
                  {availabilityOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setAvailability(option.value)}
                      className={`w-full p-4 rounded-xl border-2 transition-all cursor-pointer whitespace-nowrap ${
                        availability === option.value
                          ? 'border-green-600 bg-green-50'
                          : 'border-gray-200 hover:border-green-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="text-left">
                          <div className={`font-medium text-sm mb-1 ${
                            availability === option.value ? 'text-green-600' : 'text-gray-700'
                          }`}>
                            {option.label}
                          </div>
                          <div className="text-xs text-gray-500">
                            {option.description}
                          </div>
                        </div>
                        {availability === option.value && (
                          <i className="ri-check-line text-xl text-green-600"></i>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Results Section */}
            <div>
              <div className="bg-gradient-to-br from-green-600 to-green-800 p-8 rounded-2xl shadow-lg text-white mb-6">
                <h2 className="text-2xl font-bold mb-6">
                  Your Estimated Earnings
                </h2>

                <div className="space-y-6">
                  <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl">
                    <div className="text-sm text-green-100 mb-2">Per Week</div>
                    <div className="text-4xl font-bold">
                      ${earnings.weekly}
                    </div>
                  </div>

                  <div className="bg-white/20 backdrop-blur-sm p-6 rounded-xl border-2 border-white/30">
                    <div className="text-sm text-green-100 mb-2">Per Month</div>
                    <div className="text-5xl font-bold">
                      ${earnings.monthly}
                    </div>
                  </div>

                  <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl">
                    <div className="text-sm text-green-100 mb-2">Per Year</div>
                    <div className="text-4xl font-bold">
                      ${earnings.yearly.toLocaleString()}
                    </div>
                  </div>
                </div>

                <div className="mt-8 p-4 bg-white/10 rounded-xl">
                  <div className="flex items-start gap-3">
                    <i className="ri-information-line text-xl text-green-200 mt-1"></i>
                    <p className="text-sm text-green-100 leading-relaxed">
                      These are estimated earnings based on similar listings in your area. 
                      Actual earnings may vary based on demand, seasonality, and your specific location.
                    </p>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  Ready to start earning?
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  List your space on STASHLY and start earning passive income today. 
                  It only takes 15 minutes to create your listing.
                </p>
                <Link
                  to="/list-space"
                  className="block w-full px-6 py-4 bg-green-600 text-white font-semibold rounded-xl hover:bg-green-700 transition-colors text-center cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-circle-line mr-2"></i>
                  List Your Space Now
                </Link>
                <Link
                  to="/become-host"
                  className="block w-full px-6 py-4 mt-3 border-2 border-gray-200 text-gray-700 font-semibold rounded-xl hover:border-green-600 hover:text-green-600 transition-colors text-center cursor-pointer whitespace-nowrap"
                >
                  Learn More About Hosting
                </Link>
              </div>

              {/* Additional Info */}
              <div className="mt-6 bg-gray-50 p-6 rounded-xl">
                <h4 className="font-bold text-gray-900 mb-3">
                  What affects your earnings?
                </h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 mt-1"></i>
                    <span>Location and local demand</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 mt-1"></i>
                    <span>Size and type of space</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 mt-1"></i>
                    <span>Features and amenities</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 mt-1"></i>
                    <span>Availability and flexibility</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 mt-1"></i>
                    <span>Quality of photos and description</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Real Hosts, Real Earnings
            </h2>
            <p className="text-lg text-gray-600">
              See what other hosts are earning on STASHLY
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-8 rounded-2xl">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <i className="ri-user-line text-2xl text-green-600"></i>
                </div>
                <div>
                  <div className="font-bold text-gray-900">Sarah M.</div>
                  <div className="text-sm text-gray-600">Sydney</div>
                </div>
              </div>
              <p className="text-gray-600 mb-4 leading-relaxed">
                "I rent out my garage and make $850/month. It's completely passive income!"
              </p>
              <div className="text-2xl font-bold text-green-600">
                $850/month
              </div>
            </div>

            <div className="bg-gray-50 p-8 rounded-2xl">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <i className="ri-user-line text-2xl text-green-600"></i>
                </div>
                <div>
                  <div className="font-bold text-gray-900">James T.</div>
                  <div className="text-sm text-gray-600">Melbourne</div>
                </div>
              </div>
              <p className="text-gray-600 mb-4 leading-relaxed">
                "My parking space earns me $600/month. Best decision I've made!"
              </p>
              <div className="text-2xl font-bold text-green-600">
                $600/month
              </div>
            </div>

            <div className="bg-gray-50 p-8 rounded-2xl">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <i className="ri-user-line text-2xl text-green-600"></i>
                </div>
                <div>
                  <div className="font-bold text-gray-900">Emma L.</div>
                  <div className="text-sm text-gray-600">Brisbane</div>
                </div>
              </div>
              <p className="text-gray-600 mb-4 leading-relaxed">
                "I list my workshop space and earn $1,200/month. Amazing platform!"
              </p>
              <div className="text-2xl font-bold text-green-600">
                $1,200/month
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
